import { useTranslation } from 'react-i18next'

import { useMutateChoosePaymentPlugin } from '@entities/Checkout'

import { useErrorInfoModal } from '@features/modal'

import { PaymentPluginId, URL_VERIFY_PAYMENT } from '@shared/contants'

export const useAddPayment = () => {
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const [ choosePlugin, { loading } ] = useMutateChoosePaymentPlugin()

  const addPayment = async (
    paymentPluginId: PaymentPluginId,
    orderId?: string,
  ) => {
    try {
      // Verification URL will be created after we get operationId
      let verificationUrl: URL | null = null

      const { data } = await choosePlugin({
        variables: {
          paymentPluginId,
          order_id: orderId,
          source: 'client'
        },
      })

      const response = data?.choosePaymentPlugin
      if (!response?.type || !response?.payload) return

      const encodedPayload =
        response.payload[response.type as keyof typeof response.payload]
      if (!encodedPayload) return

      const payloadData = JSON.parse(encodedPayload)

      // Create verification URL with operationId in path
      if (response.operationId) {
        verificationUrl = new URL(
          `${URL_VERIFY_PAYMENT}/${response.operationId}`,
          window.location.origin,
        )
      }

      // Временная заглушка: определяем тип 'qr' для gpb_sbp, пока сервер не вернет правильный type
      const paymentType = paymentPluginId === 'gpb_sbp' ? 'qr' : response.type

      // Универсальная обработка на основе type
      if (paymentType === 'redirect') {
        // Редирект на внешний URL (например, страница оплаты банка)
        if (payloadData.url) {
          window.location.href = payloadData.url
        }
        return {
          type: 'redirect',
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }
      else if (paymentType === 'qr') {
        // QR код - показываем на desktop, редиректим на мобильном
        return {
          type: 'qr',
          qrUrl: payloadData.url,
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }
      else if (paymentType === 'controllable') {
        // Кастомизированный компонент (как epayment)
        return {
          type: 'controllable',
          payload: payloadData,
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }
      else if (paymentType === 'embed') {
        // Embed фрейм
        return {
          type: 'embed',
          payload: payloadData,
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }

      // Fallback для неизвестных типов
      return {
        type: response.type,
        operationId: response.operationId,
        verificationUrl: verificationUrl?.toString(),
      }
    }
    catch (error) {
      console.error('Failed to add or process payment:', error)
      openErrorInfoModal({
        description: te('add_payment'),
      })
    }
  }

  return { addPayment, loading }
}
